import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  AttendanceHeader,
  IdlePhase,
  CheckInQuestionPhase,
  CheckInSuccessPhase,
  LoadingPhase,
  WorkingPhase,
  CheckOutSummaryPhase,
  CheckOutSuccessPhase
} from './attendance';
import {
  AttendanceStateProvider,
  useAttendanceState,
  useAttendanceActions,
  useCurrentPhase
} from '../hooks/use-attendance-state';
import {
  useAttendanceBusiness,
  useAttendanceTimeTracker,
  useWorkDurationCalculator
} from '../hooks/use-attendance-business';

interface TaskAttendanceProps {
  shiftId?: string;
  jobId?: string;
  onBack?: () => void;
  onComplete?: (success: boolean) => void;
}

function AttendanceContent({
  shiftId,
  jobId,
  onBack,
  onComplete
}: TaskAttendanceProps) {
  const phase = useCurrentPhase();
  const { state } = useAttendanceState();
  const { actions } = useAttendanceActions();
  const currentTime = useAttendanceTimeTracker();

  const [userAnswer, setUserAnswer] = useState('');
  const [workSummary, setWorkSummary] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const business = useAttendanceBusiness({
    shiftId,
    jobId,
    onComplete
  });

  useEffect(() => {
    business.loadAttendanceStatus();
  }, [business]);

  useEffect(() => {
    if (phase === 'working') {
      const stopHeartbeat = business.startHeartbeat({ shiftId, jobId });
      return stopHeartbeat;
    }
  }, [phase, business, shiftId, jobId]);

  useEffect(() => {
    if (state.todayAttendance) {
      actions.setTodayAttendance(state.todayAttendance);
    }
  }, [state.todayAttendance, actions]);

  const workDuration = useWorkDurationCalculator(state.workStartTime, currentTime);

  const handleStartCheckIn = useCallback(async () => {
    actions.startCheckIn();

    try {
      const question = await business.getCheckInQuestion();
      actions.setTaskQuestion(question);
    } catch (error: any) {
      toast.error(error.message || '获取任务问题失败，请重试');
      actions.reset();
    }
  }, [actions, business]);

  const handleSubmitCheckIn = useCallback(async () => {
    if (!state.taskQuestion) return;

    actions.submitCheckIn();
    setIsSubmitting(true);

    try {
      const result = await business.submitCheckIn({
        userAnswer,
        question: state.taskQuestion
      });
      actions.checkInSuccess(result);
      setUserAnswer('');
    } catch (error: any) {
      toast.error(error.message || '上班打卡失败，请重试');
      actions.checkInError(error.message || '打卡失败');
    } finally {
      setIsSubmitting(false);
    }
  }, [actions, business, userAnswer, state.taskQuestion]);

  const handleStartWork = useCallback(() => {
    actions.startWork();
  }, [actions]);

  const handleStartCheckOut = useCallback(async () => {
    actions.startCheckOut();

    try {
      const question = await business.getCheckOutQuestion();
      actions.setSummaryQuestion(question);
    } catch (error: any) {
      toast.error(error.message || '获取总结问题失败，请重试');
      actions.startWork();
    }
  }, [actions, business]);

  const handleSubmitCheckOut = useCallback(async () => {
    actions.submitCheckOut();
    setIsSubmitting(true);

    try {
      const result = await business.submitCheckOut(workSummary);
      actions.checkOutSuccess(result);
      setWorkSummary('');
    } catch (error: any) {
      toast.error(error.message || '下班打卡失败，请重试');
      actions.checkOutError(error.message || '打卡失败');
    } finally {
      setIsSubmitting(false);
    }
  }, [actions, business, workSummary]);

  const handleCancel = useCallback(() => {
    actions.reset();
    setUserAnswer('');
    setWorkSummary('');
  }, [actions]);

  const handleComplete = useCallback(() => {
    onComplete?.(true);
  }, [onComplete]);

  const renderContent = () => {
    switch (phase) {
      case 'idle':
        return (
          <IdlePhase
            onStart={handleStartCheckIn}
            locationLoading={business.locationLoading}
            locationError={business.locationError}
          />
        );

      case 'checkin-loading':
        return <LoadingPhase message="准备打卡问题中..." color="blue" />;

      case 'checkin-question':
      case 'checkin-answering':
        return state.taskQuestion ? (
          <CheckInQuestionPhase
            question={state.taskQuestion}
            answer={userAnswer}
            onAnswerChange={setUserAnswer}
            onSubmit={handleSubmitCheckIn}
            onCancel={handleCancel}
            location={business.location}
            isSubmitting={isSubmitting}
          />
        ) : null;

      case 'checkin-processing':
        return <LoadingPhase message="验证打卡信息中..." color="blue" />;

      case 'checkin-success':
        return state.checkInResult ? (
          <CheckInSuccessPhase
            result={state.checkInResult}
            onStartWork={handleStartWork}
          />
        ) : null;

      case 'working':
        return (
          <WorkingPhase
            workDuration={workDuration}
            checkInTime={state.todayAttendance?.checkInTime}
            onCheckOut={handleStartCheckOut}
          />
        );

      case 'checkout-loading':
        return <LoadingPhase message="准备总结问题中..." color="orange" />;

      case 'checkout-summary':
        return state.summaryQuestion ? (
          <CheckOutSummaryPhase
            question={state.summaryQuestion}
            summary={workSummary}
            onSummaryChange={setWorkSummary}
            onSubmit={handleSubmitCheckOut}
            onCancel={handleStartWork}
            location={business.location}
            isSubmitting={isSubmitting}
          />
        ) : null;

      case 'checkout-processing':
        return <LoadingPhase message="计算工时并生成结算中..." color="orange" />;

      case 'checkout-success':
        return state.checkOutResult ? (
          <CheckOutSuccessPhase
            result={state.checkOutResult}
            todayAttendance={state.todayAttendance}
            onComplete={handleComplete}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="wx-page bg-white">
      <AttendanceHeader onBack={onBack} />
      {renderContent()}
    </div>
  );
}

export function TaskAttendance(props: TaskAttendanceProps) {
  return (
    <AttendanceStateProvider>
      <AttendanceContent {...props} />
    </AttendanceStateProvider>
  );
}

export default TaskAttendance;
